import { Cloud, CloudOff, Loader2, Check } from "lucide-react";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export type SyncStatus = 'idle' | 'syncing' | 'synced' | 'error' | 'offline';

interface CloudSyncIndicatorProps {
  isLoading: boolean;
  syncStatus: SyncStatus;
  isAuthenticated: boolean;
}

export const CloudSyncIndicator = ({ 
  isLoading, 
  syncStatus, 
  isAuthenticated 
}: CloudSyncIndicatorProps) => {
  const [visible, setVisible] = useState(true);
  
  // Auto-fade synced status after 3 seconds
  useEffect(() => {
    if (syncStatus === 'synced') {
      const timer = setTimeout(() => setVisible(false), 3000);
      return () => clearTimeout(timer);
    } else {
      setVisible(true);
    }
  }, [syncStatus]);
  
  // Don't render for guests or when idle and not visible
  if (!isAuthenticated) return null;
  if (syncStatus === 'idle' && !isLoading) return null;
  if (syncStatus === 'synced' && !visible) return null;
  
  const getStatusConfig = () => {
    if (isLoading) {
      return {
        icon: <Loader2 className="h-3.5 w-3.5 animate-spin" />,
        text: "Loading...",
        tooltip: "Loading from cloud...",
        className: "text-muted-foreground"
      };
    }
    
    switch (syncStatus) {
      case 'syncing':
        return {
          icon: <Loader2 className="h-3.5 w-3.5 animate-spin" />,
          text: "Saving...",
          tooltip: "Saving to cloud...",
          className: "text-primary"
        };
      case 'synced':
        return {
          icon: (
            <span className="relative">
              <Cloud className="h-3.5 w-3.5" />
              <Check className="h-2 w-2 absolute -bottom-0.5 -right-0.5 text-green-500" />
            </span>
          ),
          text: "Saved",
          tooltip: "Saved to cloud",
          className: "text-green-600 dark:text-green-400"
        };
      case 'error':
        return {
          icon: <CloudOff className="h-3.5 w-3.5" />,
          text: "Save failed",
          tooltip: "Failed to save - will retry",
          className: "text-destructive"
        };
      case 'offline':
        return {
          icon: <CloudOff className="h-3.5 w-3.5" />,
          text: "Offline",
          tooltip: "Offline mode - saving locally",
          className: "text-warning"
        };
      default:
        return null;
    }
  };
  
  const config = getStatusConfig();
  if (!config) return null;
  
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div 
          className={cn(
            "flex items-center gap-1.5 text-xs transition-all duration-300 cursor-default",
            config.className,
            syncStatus === 'synced' && !visible && "opacity-0"
          )}
          aria-live="polite"
        >
          {config.icon}
          <span className="hidden sm:inline">{config.text}</span>
        </div>
      </TooltipTrigger>
      <TooltipContent>
        <p>{config.tooltip}</p>
      </TooltipContent>
    </Tooltip>
  );
};
